import { Formik } from "formik";
import { useRef } from "react";
import ResultStep from "../StepComponents/ResultStep";
import PersonalDataStep from "../StepComponents/PersonalDataStep";
import {
  addressValidationSchema,
  bankDataValidationSchema,
  personalDataValidationSchema,
} from "../validations";
import { FormValues } from "@/models/proposalForm.model";
import AddressStep from "../StepComponents/AddressStep";
import BankDataStep from "../StepComponents/BankDataStep";
import ConfirmationStep from "../StepComponents/ConfirmationStep";
import { useNavigate, useParams } from "react-router-dom";

export const getProposalFormSteps = (
  formData: FormValues,
  updateFormData: (
    step: keyof FormValues,
    values: Partial<FormValues[keyof FormValues]>
  ) => void,
  handleNext: () => void,
  handleBack: () => void,
  handleSubmit: () => void
) => {
  const personalDataFormikRef = useRef<any>(null);
  const addressFormikRef = useRef<any>(null);
  const bankDataFormikRef = useRef<any>(null);
  const { shorthash } = useParams<{ shorthash: string }>();
  const navigate = useNavigate();

  return [
    {
      component: (
        <ResultStep
          key={0}
          data={formData.resultStep}
          proposal={formData.proposal}
          updateData={(values) => updateFormData("resultStep", values)}
          handleNext={handleNext}
          handleBack={() => navigate(`/${shorthash}`)}
        />
      ),
      isFormikStep: false,
      submitForm: null,
    },
    {
      component: (
        <Formik<FormValues["personalData"]>
          key={1}
          innerRef={personalDataFormikRef}
          initialValues={
            formData.personalData ?? {
              name: "",
              birthDate: "",
              cellphone: "",
              motherName: "",
            }
          }
          validationSchema={personalDataValidationSchema}
          onSubmit={(values) => {
            updateFormData("personalData", values);
            handleNext();
          }}
        >
          {(formik) => (
            <PersonalDataStep formik={formik} handleBack={handleBack} />
          )}
        </Formik>
      ),
      isFormikStep: true,
      submitForm: () => {
        if (personalDataFormikRef.current) {
          personalDataFormikRef.current.handleSubmit();
        }
      },
    },
    {
      component: (
        <Formik<FormValues["address"]>
          key={2}
          innerRef={addressFormikRef}
          initialValues={
            formData.address ?? {
              street: "",
              number: 0,
              cep: "",
              district: "",
              complement: "",
              city: "",
              state: "",
            }
          }
          validationSchema={addressValidationSchema}
          onSubmit={(values) => {
            updateFormData("address", values);
            handleNext();
          }}
        >
          {(formik) => <AddressStep formik={formik} handleBack={handleBack} />}
        </Formik>
      ),
      isFormikStep: true,
      submitForm: () => {
        if (addressFormikRef.current) {
          addressFormikRef.current.handleSubmit();
        }
      },
    },
    {
      component: (
        <Formik<FormValues["bankData"]>
          key={3}
          innerRef={bankDataFormikRef}
          initialValues={
            formData.bankData ?? {
              paymentType: "",
              pixType: "",
              pixKey: "",
              bank: "",
              accountType: "",
              account: "",
              agency: "",
              digit: "",
            }
          }
          validationSchema={bankDataValidationSchema}
          onSubmit={(values) => {
            updateFormData("bankData", values);
            handleNext();
          }}
        >
          {(formik) => (
            <BankDataStep
              formik={formik}
              handleBack={handleBack}
              proposal={formData.proposal}
            />
          )}
        </Formik>
      ),
      isFormikStep: true,
      submitForm: () => {
        if (bankDataFormikRef.current) {
          bankDataFormikRef.current.handleSubmit();
        }
      },
    },
    {
      component: (
        <ConfirmationStep
          key={4}
          formData={formData}
          handleSubmit={handleSubmit}
          proposal={formData.proposal}
        />
      ),
      isFormikStep: false,
      submitForm: null,
    },
  ];
};
